import { ChangeEvent, useState, useTransition } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import { SearchBar } from '@mint-lib/tw-components/patterns';

import type { GlossaryItem } from '../../api/types.js';
import { usePlatformsCatalog } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import type { PlatformGlossaryOptionV2 } from '../types.js';
import { GlossarySelectionListItem } from './GlossarySelectionListItem.jsx';

type PlatformsGlossaryListProps = {
  glossaries: GlossaryItem[];
  selectedItems: string[];
  onItemSelect: (name: string) => () => void;
};

export const PlatformsGlossaryListV2 = ({
  glossaries,
  selectedItems,
  onItemSelect,
}: PlatformsGlossaryListProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    data: { platformsList },
  } = usePlatformsCatalog();

  const [, startTransition] = useTransition();

  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchValue(e.target.value);
    });
  };
  const handleSearchClear = () => {
    setSearchValue('');
  };

  const filteredPlatformsList = platformsList.reduce((acc, platform) => {
    const isMatchingSearchValue = platform.label
      .trim()
      .toLowerCase()
      .includes(searchValue.trim().toLowerCase());

    if (isMatchingSearchValue) {
      acc.push({
        ...platform,
        name: platform.name,
        label: platform.label,
        logo: <Settings className="fill-icon-primary" />,
        type: null,
        readOnly: glossaries.some(
          function isPlatformSelected(platformGlossary) {
            return platformGlossary.name === platform.name;
          },
        ),
      });
    }
    return acc;
  }, [] as PlatformGlossaryOptionV2[]);

  return (
    <>
      <SearchBar
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholder={t('Search by name')}
        size="small"
        className="mb-2 min-h-8 w-full"
      />
      <div className="flex h-full flex-col gap-6">
        <div className="flex flex-col gap-2">
          {filteredPlatformsList.map(({ name, label, logo, readOnly }) => (
            <GlossarySelectionListItem
              key={name}
              glossaryType="platform"
              label={label}
              icon={logo}
              readOnly={readOnly}
              selected={selectedItems.includes(name)}
              onSelect={onItemSelect(name)}
            />
          ))}
        </div>
        {searchValue && !filteredPlatformsList.length ? (
          <div className="flex h-full flex-col justify-center">
            <EmptyState
              icon="IllustrationNoResults"
              text={t('No results found for "{{searchValue}}"', {
                searchValue,
              })}
              description={t('Please, try searching for something different.')}
              className="border-none shadow-none"
            />
          </div>
        ) : null}
      </div>
    </>
  );
};
